import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Plus, FolderKanban, ArrowRight, Loader2, CreditCard, ReceiptText } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import BuyCreditsModal from '../components/BuyCreditsModal';
import { Button } from '../components/ui/button';
import { workspaceAPI, paymentsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { STAGES, computeStages, STATUS_COLORS, STATUS_LABELS } from '../lib/pipeline';

function ProjectCard({ project }) {
  const { lead } = project;
  const stages = computeStages(project);
  const title = stages.build?.project_name && stages.build.status === 'ready'
    ? stages.build.project_name
    : (lead.project_type || 'Project');

  return (
    <Link
      to={`/workspace/project/${lead.id}`}
      className="block rounded-xl border p-6 transition-all hover:-translate-y-0.5 hover:shadow-lg"
      style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
      data-testid={`project-card-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading font-bold text-lg capitalize truncate" style={{ color: 'var(--text-primary)' }}>
            {title}
          </h3>
          <p className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--text-muted)' }}>
            {lead.description}
          </p>
        </div>
        <ArrowRight className="w-5 h-5 shrink-0 mt-1" style={{ color: 'var(--primary-accent)' }} />
      </div>

      {/* Stage pipeline */}
      <div className="flex items-center mt-5">
        {STAGES.map((stage, i) => {
          const status = stages[stage.key];
          return (
            <React.Fragment key={stage.key}>
              {i > 0 && <div className="flex-1 h-px mx-1" style={{ background: 'var(--border)' }} />}
              <div className="flex flex-col items-center gap-1" title={`${stage.label}: ${STATUS_LABELS[status]}`}>
                <span
                  className={`w-2.5 h-2.5 rounded-full ${status === 'generating' ? 'animate-pulse' : ''}`}
                  style={{ background: STATUS_COLORS[status] }}
                />
                <span className="text-[9px] font-medium uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                  {stage.label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </Link>
  );
}

const TXN_LABELS = {
  prototype_full_generation: 'Full Prototype',
  dev_build_generation: 'Development Build',
  ai_qa_test_report: 'AI QA & Test Report',
  release_packaging: 'Release Package',
};

const txnLabel = (reason = '') =>
  TXN_LABELS[reason] || (reason.startsWith('credit_pack_purchase') ? 'Credit Pack Purchase' : reason.replace(/_/g, ' '));

export default function WorkspacePage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuy, setShowBuy] = useState(false);

  useEffect(() => {
    Promise.all([
      workspaceAPI.getProjects().then((res) => setProjects(res.data.projects || [])),
      paymentsAPI.getTransactions().then((res) => setTransactions(res.data.transactions || [])),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO title="My Workspace | XceedAgents.ai" description="Manage your projects from proposal to release." />
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 py-12" data-testid="workspace-page">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--primary-accent)' }}>
              Client Workspace
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>
              Run every project from proposal to release — AI agents build, humans verify.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm"
              style={{ background: 'var(--tag-bg)', color: 'var(--primary-accent)' }}
              data-testid="workspace-credits"
            >
              <Sparkles className="w-4 h-4" /> {user?.credits ?? 0} credits
            </div>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setShowBuy(true)}
              data-testid="buy-credits-btn"
            >
              <CreditCard className="w-4 h-4" /> Buy Credits
            </Button>
            <Link to="/start-project">
              <Button className="gap-2 font-semibold text-white" style={{ background: 'var(--primary-accent)' }} data-testid="new-project-btn">
                <Plus className="w-4 h-4" /> New Project
              </Button>
            </Link>
          </div>
        </div>

        {/* Projects */}
        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary-accent)' }} />
          </div>
        ) : projects.length === 0 ? (
          <div
            className="rounded-2xl border border-dashed p-16 text-center"
            style={{ borderColor: 'var(--border-strong)' }}
            data-testid="workspace-empty-state"
          >
            <FolderKanban className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
            <h2 className="font-heading text-xl font-bold mb-2">No projects yet</h2>
            <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: 'var(--text-muted)' }}>
              Describe your idea to our AI and get a free proposal in minutes. Then take it through
              prototype, development, testing and release — right here.
            </p>
            <Link to="/start-project">
              <Button className="font-semibold text-white gap-2" style={{ background: 'var(--primary-accent)' }}>
                <Plus className="w-4 h-4" /> Start your first project
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((p) => (
              <ProjectCard key={p.lead.id} project={p} />
            ))}
          </div>
        )}

        {/* Credit Management */}
        <div className="mt-12 grid lg:grid-cols-[320px_1fr] gap-5" data-testid="credit-management">
          <div className="rounded-xl border p-6 flex flex-col" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)' }}>
              Credit Balance
            </p>
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6" style={{ color: 'var(--primary-accent)' }} />
              <span className="font-heading text-4xl font-bold" data-testid="credit-balance">{user?.credits ?? 0}</span>
              <span className="text-sm" style={{ color: 'var(--text-muted)' }}>credits</span>
            </div>
            <p className="text-xs mt-3 flex-1" style={{ color: 'var(--text-muted)' }}>
              Full prototype 150 · Development 500 · AI QA 200 · Release 100
            </p>
            <Button
              className="mt-4 w-full gap-2 font-semibold text-white"
              style={{ background: 'var(--primary-accent)' }}
              onClick={() => setShowBuy(true)}
              data-testid="buy-credits-panel-btn"
            >
              <CreditCard className="w-4 h-4" /> Buy Credits
            </Button>
          </div>

          <div className="rounded-xl border p-6" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
              <ReceiptText className="w-4 h-4" /> Transaction History
            </p>
            {transactions.length === 0 ? (
              <p className="text-sm py-6 text-center" style={{ color: 'var(--text-muted)' }} data-testid="txn-empty">
                No transactions yet. Purchases and credit spends will appear here.
              </p>
            ) : (
              <div className="max-h-72 overflow-y-auto -mx-2" data-testid="txn-list">
                {transactions.map((t, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-2 py-2.5 border-b last:border-0" style={{ borderColor: 'var(--border)' }}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium capitalize truncate" style={{ color: 'var(--text-primary)' }}>{txnLabel(t.reason)}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {t.created_at ? new Date(t.created_at).toLocaleString() : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold" style={{ color: t.amount > 0 ? '#10B981' : 'var(--error)' }}>
                        {t.amount > 0 ? '+' : ''}{t.amount}
                      </p>
                      <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>bal {t.balance_after}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Lifecycle legend */}
        <div className="mt-12 rounded-xl border p-6" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)' }}>
            How a project flows
          </p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <span className="font-semibold">Proposal (free)</span>
            <ArrowRight className="w-3.5 h-3.5" />
            <span className="font-semibold">Prototype (teaser free · full 150 cr)</span>
            <ArrowRight className="w-3.5 h-3.5" />
            <span className="font-semibold">Development build (500 cr)</span>
            <ArrowRight className="w-3.5 h-3.5" />
            <span className="font-semibold">AI QA & Testing (200 cr)</span>
            <ArrowRight className="w-3.5 h-3.5" />
            <span className="font-semibold">Release package (100 cr)</span>
          </div>
        </div>
      </main>

      <Footer />
      <BuyCreditsModal open={showBuy} onOpenChange={setShowBuy} />
    </div>
  );
}
